import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { theme } from './layout';
import './App.css';
import { Router } from './routes';
import { apiClient, setCsrfToken } from './libs/authHeader';
import { Toast } from './components';
import { Severity } from './components/Toast/type';

function App() {
  const [csrfError, setCsrfError] = useState<string | null>(null);

  // Fetch CSRF token on app initialization with retry logic
  // Backend will set the XSRF-TOKEN cookie automatically in the response
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;

    const initCsrfToken = async () => {
      try {
        // Make a request to backend - it will set the CSRF cookie automatically
        const response = await apiClient.get('/csrf-token');

        // Validate response structure
        if (!response?.data || typeof response.data !== 'object') {
          throw new Error('Invalid CSRF token response structure');
        }

        const { csrfToken } = response.data;

        // Validate token is a non-empty string
        if (typeof csrfToken === 'string' && csrfToken.length > 0) {
          // Store in memory as fallback (for localhost or when cookies are blocked)
          setCsrfToken(csrfToken);
          setCsrfError(null); // Clear any previous errors
        } else {
          throw new Error('Invalid CSRF token format');
        }
      } catch (error) {
        console.warn(`Failed to initialize CSRF token (attempt ${retryCount + 1}/${maxRetries}):`, error);

        // Retry with exponential backoff
        if (retryCount < maxRetries) {
          retryCount += 1;
          const backoffDelay = Math.min(1000 * 2 ** (retryCount - 1), 5000); // 1s, 2s, 4s max
          setTimeout(() => initCsrfToken(), backoffDelay);
        } else {
          // All retries exhausted - show error to user
          setCsrfError('Unable to initialize security token. Some features may not work correctly. Please refresh the page.');
        }
      }
    };
    initCsrfToken();
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Router />
      </BrowserRouter>
      {csrfError && <Toast open={!!csrfError} message={csrfError} severity={Severity.ERROR} onClose={() => setCsrfError(null)} />}
    </ThemeProvider>
  );
}

export default App;
